import { useEffect, useState } from 'react';
import { PublicAchievementsCard } from '../achievements/PublicAchievementsCard';
import { MediaGallery } from '../media/MediaGallery';
import { fetchPublicProfile, type PublicProfileData } from '../profile/publicProfileApi';
import { replaceOwnerProfilePath, syncShellHistoryFromLocation } from '../shellHistory';
import { DesktopThreeColumnLayout } from '../ui/DesktopThreeColumnLayout';
import { HopperIcon } from '../ui/icons/HopperIcon';
import { FollowButton } from './FollowButton';
import { UserAvatar } from './UserAvatar';
import '../profile-family.css';

type UserProfileScreenProps = {
  profileReference?: string;
  userId?: string;
  authenticated: boolean;
  currentUserId: string | null;
  onBack: () => void;
  onOpenFollowers: (userId: string) => void;
  onOpenFollowing: (userId: string) => void;
};

function profileName(profile: PublicProfileData): string {
  return profile.display_name?.trim() || 'Гость Хоппера';
}

function levelText(level: number | null): string {
  return level === null ? 'Уровень не определён' : `Уровень ${level}`;
}

function ProfileFamilyLoading() {
  return (
    <main className="self-profile-page public-profile-page" aria-label="Загружаем профиль">
      <DesktopThreeColumnLayout
        className="self-profile-layout public-profile-layout"
        left={(
          <section className="profile-reference-card profile-left-identity profile-reference-skeleton" aria-hidden="true">
            <span className="social-skeleton social-skeleton--avatar is-profile" />
            <span className="social-skeleton social-skeleton--line is-medium" />
            <span className="social-skeleton social-skeleton--line is-short" />
          </section>
        )}
        right={(
          <section className="profile-reference-card profile-reference-skeleton profile-tall-skeleton" aria-hidden="true">
            <span className="social-skeleton social-skeleton--line is-medium" />
            <span className="social-skeleton social-skeleton--line" />
            <span className="social-skeleton social-skeleton--line" />
          </section>
        )}
      >
        <div className="self-profile-center" aria-hidden="true">
          <section className="profile-reference-card profile-main-header profile-reference-skeleton">
            <span className="social-skeleton social-skeleton--avatar is-profile" />
            <span className="social-skeleton social-skeleton--line is-medium" />
            <span className="social-skeleton social-skeleton--line" />
          </section>
          <section className="profile-reference-card profile-reference-skeleton profile-tall-skeleton">
            <span className="social-skeleton social-skeleton--line is-medium" />
            <span className="social-skeleton social-skeleton--line" />
          </section>
        </div>
      </DesktopThreeColumnLayout>
    </main>
  );
}

export function UserProfileScreen({
  profileReference,
  userId,
  authenticated,
  currentUserId,
  onBack,
  onOpenFollowers,
  onOpenFollowing,
}: UserProfileScreenProps) {
  const reference = profileReference ?? userId ?? '';
  const [profile, setProfile] = useState<PublicProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProfile(null);

    if (!reference) {
      setLoading(false);
      setError('Профиль пользователя не найден.');
      return () => { cancelled = true; };
    }

    void fetchPublicProfile(reference)
      .then((response) => {
        if (!cancelled) setProfile(response);
      })
      .catch(() => {
        if (!cancelled) setError('Профиль пользователя не найден или временно недоступен.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [reference]);

  const isOwner = Boolean(profile && currentUserId && currentUserId === profile.id);

  useEffect(() => {
    if (!profile || !isOwner) return;

    const canonicalReference = profile.slug?.trim() || profile.id;
    replaceOwnerProfilePath(canonicalReference);
    syncShellHistoryFromLocation();
  }, [isOwner, profile]);

  const handleBack = () => {
    const state = window.history.state as { hopperProfileReference?: unknown; hopperOwnerProfile?: unknown } | null;
    if (state?.hopperProfileReference && state.hopperOwnerProfile !== true && window.history.length > 1) {
      window.history.back();
      return;
    }
    onBack();
  };

  if (loading || isOwner) {
    return <ProfileFamilyLoading />;
  }

  if (error || !profile) {
    return (
      <main className="self-profile-page public-profile-page" aria-label="Профиль недоступен">
        <div className="public-profile-state-wrap">
          <button className="social-back profile-family-back" type="button" onClick={handleBack}>
            <HopperIcon name="arrow-left" size={17} />
            <span>Назад</span>
          </button>
          <section className="profile-reference-card public-profile-state-card">
            <HopperIcon name="warning" size={28} />
            <strong>{error ?? 'Профиль недоступен'}</strong>
            <span>Попробуйте открыть профиль ещё раз позже.</span>
          </section>
        </div>
      </main>
    );
  }

  const name = profileName(profile);
  const slug = profile.slug?.trim() || null;
  const statusName = profile.status?.name?.trim() || null;

  if (profile.profile_hidden) {
    return (
      <main className="self-profile-page public-profile-page" aria-label="Скрытый профиль">
        <DesktopThreeColumnLayout
          className="self-profile-layout public-profile-layout"
          left={<div />}
          right={<div />}
        >
          <div className="self-profile-center">
            <button className="social-back profile-family-back" type="button" onClick={handleBack}>
              <HopperIcon name="arrow-left" size={17} />
              <span>Назад</span>
            </button>
            <section className="profile-reference-card profile-main-header public-profile-hidden-card">
              <div className="profile-main-header__identity">
                <UserAvatar displayName={profile.display_name} avatarUrl={profile.avatar_url} size={112} />
                <div className="profile-main-header__copy">
                  <span className="profile-main-header__eyebrow">Публичный профиль</span>
                  <h1>{name}</h1>
                </div>
              </div>
              <div className="public-profile-hidden-message">
                <HopperIcon name="eye-off" size={24} />
                <div>
                  <strong>Профиль скрыт пользователем</strong>
                  <span>Гость отключил отображение профиля в публичных разделах Хоппера.</span>
                </div>
              </div>
            </section>
          </div>
        </DesktopThreeColumnLayout>
      </main>
    );
  }

  const leftColumn = (
    <div className="self-profile-left">
      <section className="profile-reference-card profile-left-identity public-profile-left-card">
        <UserAvatar displayName={profile.display_name} avatarUrl={profile.avatar_url} size={88} />
        <div className="profile-left-identity__copy">
          <h1>{name}</h1>
          {slug && <span>@{slug}</span>}
          <strong>{statusName ?? levelText(profile.level)}</strong>
        </div>

        <nav className="profile-left-nav" aria-label="Публичные разделы профиля">
          <button type="button" onClick={() => onOpenFollowers(profile.id)}>
            <span className="profile-left-nav__icon is-friends"><HopperIcon name="users" size={19} /></span>
            <span><strong>Подписчики</strong><small>{profile.followers_count}</small></span>
            <span aria-hidden="true">›</span>
          </button>
          <button type="button" onClick={() => onOpenFollowing(profile.id)}>
            <span className="profile-left-nav__icon is-venues"><HopperIcon name="user-check" size={19} /></span>
            <span><strong>Подписки</strong><small>{profile.following_count}</small></span>
            <span aria-hidden="true">›</span>
          </button>
        </nav>
      </section>
    </div>
  );

  const centerColumn = (
    <div className="self-profile-center">
      <button className="social-back profile-family-back" type="button" onClick={handleBack}>
        <HopperIcon name="arrow-left" size={17} />
        <span>Назад</span>
      </button>

      <section className="profile-reference-card profile-main-header public-profile-main-header">
        <div className="public-profile-main-row">
          <div className="profile-main-header__identity">
            <UserAvatar displayName={profile.display_name} avatarUrl={profile.avatar_url} size={112} />
            <div className="profile-main-header__copy">
              <span className="profile-main-header__eyebrow">Публичный профиль</span>
              <h1>{name}</h1>
              {slug && <p><HopperIcon name="profile" size={16} /> @{slug}</p>}
              <strong>{statusName ?? levelText(profile.level)}</strong>
              {profile.bio && <p>{profile.bio}</p>}
            </div>
          </div>

          <div className="public-profile-header-actions">
            {profile.follows_you && <span className="follows-you-badge">Подписан(а) на вас</span>}
            {authenticated && currentUserId !== profile.id && (
              <FollowButton
                key={`${profile.id}:${profile.you_follow ? '1' : '0'}`}
                userId={profile.id}
                initialFollowing={profile.you_follow}
                onChanged={(following) => setProfile((current) => current ? {
                  ...current,
                  you_follow: following,
                  followers_count: Math.max(0, current.followers_count + (following ? 1 : -1)),
                } : current)}
              />
            )}
          </div>
        </div>

        <div className="profile-stats-row public-profile-stats" aria-label="Публичная статистика профиля">
          <button className="profile-stat-chip is-impressions" type="button" onClick={() => onOpenFollowers(profile.id)}>
            <span className="profile-stat-chip__icon"><HopperIcon name="users" size={18} /></span>
            <span><strong>{profile.followers_count}</strong><small>Подписчики</small></span>
          </button>
          <button className="profile-stat-chip is-visits" type="button" onClick={() => onOpenFollowing(profile.id)}>
            <span className="profile-stat-chip__icon"><HopperIcon name="user-check" size={18} /></span>
            <span><strong>{profile.following_count}</strong><small>Подписки</small></span>
          </button>
          <div className="profile-stat-chip is-discovered">
            <span className="profile-stat-chip__icon"><HopperIcon name="level" size={18} /></span>
            <span><strong>{profile.level ?? '—'}</strong><small>Уровень</small></span>
          </div>
        </div>
      </section>

      <PublicAchievementsCard userId={profile.id} />

      <section className="profile-reference-card profile-media-section public-profile-media-card" id="public-profile-media">
        <MediaGallery mode="user_profile" entityId={profile.id} canEdit={false} />
      </section>
    </div>
  );

  const rightColumn = (
    <div className="self-profile-right">
      <section className="profile-reference-card public-profile-context-card">
        <span className="profile-main-header__eyebrow">Профиль гостя</span>
        <h2>{name}</h2>
        {statusName && (
          <div className="public-profile-context-row">
            <HopperIcon name="trophy" size={18} />
            <span><small>Статус</small><strong>{statusName}</strong></span>
          </div>
        )}
        <div className="public-profile-context-row">
          <HopperIcon name="level" size={18} />
          <span><small>Глобальный уровень</small><strong>{profile.level ?? 'Пока не определён'}</strong></span>
        </div>
        <div className="public-profile-context-row">
          <HopperIcon name="users" size={18} />
          <span><small>Связь</small><strong>{profile.follows_you ? 'Подписан(а) на вас' : profile.you_follow ? 'Вы подписаны' : 'Нет подписки'}</strong></span>
        </div>
      </section>
    </div>
  );

  return (
    <main className="self-profile-page public-profile-page" aria-label={`Профиль ${name}`}>
      <DesktopThreeColumnLayout
        className="self-profile-layout public-profile-layout"
        left={leftColumn}
        right={rightColumn}
      >
        {centerColumn}
      </DesktopThreeColumnLayout>
    </main>
  );
}
